/**
 * Join School Page
 * Handles school invitation acceptance and inline registration for new users
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { School, Loader2, CheckCircle, XCircle, LogIn, Lock, User, Eye, EyeOff, Mail, PartyPopper } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = "/api";

export default function JoinSchool({ user, setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Registration form state for new users
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    password: "",
    confirmPassword: ""
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link");
      setLoading(false);
      return;
    }
    fetchInviteInfo();
  }, [token]);

  const fetchInviteInfo = async () => {
    try {
      const response = await fetch(`${API}/school/join/${token}`);
      if (response.ok) {
        const data = await response.json();
        setInviteInfo(data);
      } else {
        const err = await response.json();
        setError(err.detail || "Invalid or expired invitation");
      }
    } catch (err) {
      setError("Failed to load invitation");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }
    
    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle registration for new users
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setRegistering(true);
    try {
      const response = await fetch(`${API}/school/join/${token}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          invite_token: token,
          name: formData.name.trim(),
          password: formData.password
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        toast.success(data.message);
        
        // Set user and redirect
        if (setUser) {
          setUser(data.user);
        }
        
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      } else {
        toast.error(data.detail || "Failed to create account");
      }
    } catch (err) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setRegistering(false);
    }
  };

  // Handle join for existing logged-in users
  const handleJoin = async () => {
    if (!user) {
      // Show registration form for new users
      setShowRegisterForm(true);
      return;
    }

    setJoining(true);
    try {
      const response = await fetch(`${API}/school/join/${token}`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess(true);
        toast.success(data.message);
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to join school");
      }
    } catch (err) {
      toast.error("Failed to join school");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-destructive/10 flex items-center justify-center">
            <XCircle className="w-10 h-10 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold mb-4 text-foreground">Invalid Invitation</h1>
          <p className="text-muted-foreground mb-8">{error}</p>
          <Button onClick={() => navigate("/")} data-testid="go-home-btn">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/10 flex items-center justify-center">
            <PartyPopper className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold mb-4 text-foreground">Welcome!</h1>
          <p className="text-muted-foreground mb-4">
            You've successfully joined {inviteInfo?.school_name}.
          </p>
          <p className="text-sm text-muted-foreground">Redirecting...</p>
          <Loader2 className="w-5 h-5 animate-spin mx-auto mt-4 text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        <div className="bg-card rounded-2xl border border-border p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
            <School className="w-10 h-10 text-primary" />
          </div>
          
          <h1 className="text-2xl font-bold mb-2 text-foreground" data-testid="invite-title">
            You're Invited!
          </h1>
          
          <p className="text-muted-foreground mb-6">
            <strong className="text-foreground">{inviteInfo?.school_name}</strong> has invited you to join their coding program on CodingDrills.au
          </p>

          {inviteInfo?.email && (
            <p className="text-sm text-muted-foreground mb-6">
              Invitation for: <span className="text-foreground font-medium">{inviteInfo.email}</span>
            </p>
          )}

          {user ? (
            // Logged in user flow
            user.email?.toLowerCase() === inviteInfo?.email?.toLowerCase() ? (
              <Button 
                onClick={handleJoin} 
                disabled={joining} 
                className="w-full h-12"
                data-testid="join-school-btn"
              >
                {joining ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Joining...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Accept Invitation
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400 text-sm">
                  You're logged in as <strong>{user.email}</strong>, but this invitation is for <strong>{inviteInfo?.email}</strong>.
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
                    window.location.reload();
                  }}
                  className="w-full"
                >
                  Login with Different Account
                </Button>
              </div>
            )
          ) : showRegisterForm ? (
            // Registration form for new users
            <form onSubmit={handleRegister} className="space-y-4 text-left">
              <div className="text-center mb-4">
                <p className="text-sm text-muted-foreground">
                  Create your account to join {inviteInfo?.school_name}
                </p>
              </div>

              {/* Email (read-only) */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={inviteInfo?.email || ""}
                    disabled
                    className="pl-10 bg-muted"
                  />
                </div>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className={`pl-10 ${formErrors.name ? "border-destructive" : ""}`}
                    data-testid="school-invite-name-input"
                  />
                </div>
                {formErrors.name && (
                  <p className="text-xs text-destructive">{formErrors.name}</p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    className={`pl-10 pr-10 ${formErrors.password ? "border-destructive" : ""}`}
                    data-testid="school-invite-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formErrors.password && (
                  <p className="text-xs text-destructive">{formErrors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                    className={`pl-10 ${formErrors.confirmPassword ? "border-destructive" : ""}`}
                    data-testid="school-invite-confirm-password-input"
                  />
                </div>
                {formErrors.confirmPassword && (
                  <p className="text-xs text-destructive">{formErrors.confirmPassword}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-12"
                disabled={registering}
                data-testid="complete-school-registration-btn"
              >
                {registering ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Create Account & Join
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={() => setShowRegisterForm(false)}
                className="w-full text-sm text-muted-foreground hover:text-foreground"
              >
                Back
              </button>
            </form>
          ) : (
            // Initial state for non-logged in users
            <div className="space-y-4">
              <Button 
                onClick={() => setShowRegisterForm(true)} 
                className="w-full h-12"
                data-testid="create-account-btn"
              >
                <User className="w-5 h-5 mr-2" />
                Create Account & Join
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">
                    or
                  </span>
                </div>
              </div>
              
              <Button 
                variant="outline"
                onClick={() => {
                  localStorage.setItem("pendingSchoolInvite", token);
                  window.location.href = `/?showLogin=true`;
                }} 
                className="w-full h-12"
                data-testid="login-to-join-btn"
              >
                <LogIn className="w-5 h-5 mr-2" />
                I Already Have an Account
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          By joining, you agree to CodingDrills.au's Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
